<template>
  <b-container fluid class="p-0">
    <b-row no-gutters class="border-bottom" style="background-color: rgba(0, 0, 0, 0.03);" ref="header">
      <b-col md="8">
        <div class="p-2" style="display: inline-block;">
          <span v-if="selectedParameter !== null">
            <font-awesome-icon :icon="['fas', 'circle']" :style="{ color: platformColor(false) }" class="ml-1 mr-2" />
            <strong>{{ platformName(false) }}</strong>&nbsp;
            <font-awesome-icon :icon="['fas', 'angle-right']" size="sm" class="text-muted" />
            &nbsp;<span class="text-secondary">{{ entityName(false) }}</span>&nbsp;
            <font-awesome-icon :icon="['fas', 'angle-right']" size="sm" class="text-muted" />
            &nbsp;<span class="text-primary">{{ chartMetaData(false).label }}</span>
          </span>
          <span v-else>
            <font-awesome-icon :icon="['fas', 'circle']" class="text-muted ml-1 mr-2" /> No parameter selected
          </span>
          <span v-if="selectedParameterCompare !== null">
            <font-awesome-icon :icon="['fas', 'arrows-alt-h']" class="ml-3 mr-3 text-muted" />
            <font-awesome-icon :icon="['fas', 'circle']" :style="{ color: platformColor(true) }" class="mr-2" />
            <strong>{{ platformName(true) }}</strong>&nbsp;
            <font-awesome-icon :icon="['fas', 'angle-right']" size="sm" class="text-muted" />
            &nbsp;<span class="text-secondary">{{ entityName(true) }}</span>&nbsp;
            <font-awesome-icon :icon="['fas', 'angle-right']" size="sm" class="text-muted" />
            &nbsp;<span class="text-info">{{ chartMetaData(true).label }}</span>
          </span>
        </div>
      </b-col>
      <b-col class="text-right p-2 pr-3" md="4">
        <b-checkbox style="display: inline-block;" v-model="autorefresh" @change="toggleAutorefresh()" v-if="selectedParameter !== null" class="mr-3">
          <small>Auto-refresh</small>
        </b-checkbox>
        <b-button style="display: inline-block;" v-if="selectedParameter !== null" variant="light" class="mr-3" size="xs" @click="refresh()">
          <font-awesome-icon :icon="['fas', 'sync-alt']" class="mr-1" />
          Refresh
        </b-button>
        <b-button-close @click="select_parameter(null)"></b-button-close>
      </b-col>
    </b-row>
    <b-row no-gutters style="overflow: hidden;">
      <b-col>
        <div v-if="selectedParameter === null"
             style="width: 100%; display: flex;"
             :style="{ height: chartPaneHeight }">
          <div style="margin: auto;" class="text-muted">
            <strong>Please select parameter on the left to show time series</strong>
          </div>
        </div>
        <div id="chartContainer" ref="chartContainer"
             style="width: calc(100% - 20px); margin: auto;"
             :style="{ height: chartPaneHeight }">
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>

import { mapGetters, mapMutations, mapState } from 'vuex'
import Helpers from '@/mixins/helpers'
import Highcharts from 'highcharts/highstock'

require('highcharts/modules/accessibility')(Highcharts)
require('highcharts/modules/exporting')(Highcharts)
require('highcharts/modules/debugger')(Highcharts)
window.Highcharts = Highcharts

const REFRESH_INTERVAL = 10000

export default {
  name: 'ChartWidget',
  data: function () {
    return {
      paneHeight: 250,
      paneWidth: 0,
      chartVisible: false,
      chartBusy: false,
      chart: null,
      autorefresh: false,
      refreshTask: null
    }
  },
  mixins: [
    Helpers
  ],
  mounted () {
    // Set UTC time as default
    Highcharts.setOptions({
      time: {
        useUTC: false
      }
    })
    const widget = this
    // Subscribe to store mutations
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'select_parameter') {
        if (this.selectedParameter !== null) {
          if (this.chart === null && !this.chartBusy) {
            console.debug('chart widget: create chart instance')
            this.initChart()
          }
          console.log('chart widget: plotting ' + this.chartMetaData(false).label)
          this.chartBusy = true
          this.chart.showLoading('Plotting data..')

          // Simple and static data visualization
          let data = [...this.updates(this.selectedParameter[0], this.selectedParameter[1], this.selectedParameter[2])]
          if (this.chartMetaData(false).type === 'bool') {
            // Convert true / false to numbers
            data = data.map((item) => {
              return [item[0], (item[1] === true) ? 1 : 0]
            })
          }
          const series = [{
            data: data,
            name: this.chartMetaData(false).label,
            color: '#007bff',
            point: {
              events: {
                mouseOver: function () {
                  // Send selected timestamp to data model
                  const timestamp = this.x
                  const data = this.y
                  widget.$store.commit('select_timestamp', { timestamp, data })
                }
              }
            }
          }]
          this.chart.update({
            series: series,
            yAxis: {
              reversed: this.parameterReversed(false)
            },
            tooltip: {
              valueSuffix: ' ' + this.chartMetaData(false).unit
            }
          }, true)

          if (this.autorefresh) {
            // Set max extreme to current date
            const now = new Date().getTime()
            const range = this.chart.xAxis[0].getExtremes().max - this.chart.xAxis[0].getExtremes().min
            const newMin = now - range
            this.chart.xAxis[0].setExtremes(newMin, now, true, false)
          }
          // this.chart.redraw()
          this.chart.hideLoading()
          this.chartBusy = false
        } else {
          if (this.chart !== null) {
            // Remove chart properly
            console.debug('chart widget: destroy chart instance')
            this.chart.destroy()
            this.chart = null
          }
        }
      } else if (mutation.type === 'select_parameter_compare') {
        if (this.selectedParameter !== null && this.selectedParameterCompare !== null) {
          console.log('chart widget: comparing ' + this.chartMetaData(true).label)
          this.chartBusy = true
          this.chart.showLoading('Plotting data..')

          // Remove compare parameter first if available
          // if (this.chart.series.length === 2) {
          //   this.chart.series[1].remove(false)
          //   this.chart.yAxis[1].remove(false)
          // }

          // Simple and static data visualization
          let data = [...this.updates(this.selectedParameterCompare[0], this.selectedParameterCompare[1], this.selectedParameterCompare[2])]
          if (this.chartMetaData(true).type === 'bool') {
            // Convert true / false to numbers
            data = data.map((item) => {
              return [item[0], (item[1] === true) ? 1 : 0]
            })
          }

          // const yAxis = {
          //   title: {
          //     text: this.chartMetaData(true).label + ' (' + this.chartMetaData(true).unit + ')',
          //     style: { color: '#17a2b8' }
          //   },
          //   labels: {
          //     style: { color: '#17a2b8' }
          //   },
          //   id: 'second',
          //   reversed: this.parameterReversed(true),
          //   opposite: true
          // }
          if (this.chart.series.length < 2) {
            // Add new (second) series
            const series = {
              data: data
              // name: this.chartMetaData(true).label,
              // color: '#17a2b8',
              // yAxis: 'second'
            }
            // this.chart.addAxis(yAxis, false)
            this.chart.addSeries(series, false)
          } else {
            // Update second series
            // this.chart.yAxis[1].update(yAxis)
            this.chart.series[1].setData(data, false)
          }
          if (this.autorefresh) {
            // Set max extreme to current date
            this.chart.xAxis[0].setExtremes(this.chart.xAxis[0].getExtremes().min, new Date().getTime(), false)
          }
          this.chart.redraw()
          this.chart.hideLoading()
          this.chartBusy = false
        }
      }
    })
  },
  beforeDestroy () {
    // Unsubscribe from store mutations
    this.unsubscribe()
  },
  computed: {
    ...mapState(['selectedParameter', 'selectedParameterCompare']),
    ...mapGetters(['updates', 'parameter', 'entity', 'platform']),
    chartContainerClass: function () {
      if (this.chartVisible) {
        return 'd-block'
      }
      return 'd-none'
    },
    chartPaneHeight: function () {
      if (this.paneHeight > 0) {
        return this.paneHeight + 'px'
      }
      return '0px'
    },
    chartMetaData: function () {
      return second => {
        if (!second && this.selectedParameter !== null) {
          return this.parameter(this.selectedParameter[0], this.selectedParameter[1], this.selectedParameter[2])
        } else if (second && this.selectedParameterCompare !== null) {
          return this.parameter(this.selectedParameterCompare[0], this.selectedParameterCompare[1], this.selectedParameterCompare[2])
        }
        return {
          unit: '',
          label: ''
        }
      }
    },
    platformColor: function () {
      return second => {
        if (!second && this.selectedParameter !== null) {
          return this.platform(this.selectedParameter[0]).color
        } else if (second && this.selectedParameterCompare !== null) {
          return this.platform(this.selectedParameterCompare[0]).color
        }
        return ''
      }
    },
    platformName: function () {
      return second => {
        if (!second && this.selectedParameter !== null) {
          return this.platform(this.selectedParameter[0]).description
        } else if (second && this.selectedParameterCompare !== null) {
          return this.platform(this.selectedParameterCompare[0]).description
        }
        return ''
      }
    },
    entityName: function () {
      return second => {
        if (!second && this.selectedParameter !== null) {
          return this.entity(this.selectedParameter[0], this.selectedParameter[1]).label
        } else if (second && this.selectedParameterCompare !== null) {
          return this.entity(this.selectedParameterCompare[0], this.selectedParameterCompare[1]).label
        }
        return ''
      }
    },
    parameterReversed: function () {
      return second => {
        if (!second && this.selectedParameter !== null) {
          if ('axis_reversed' in this.chartMetaData(false)) {
            return this.chartMetaData(false).axis_reversed
          }
        } else if (second && this.selectedParameterCompare !== null) {
          if ('axis_reversed' in this.chartMetaData(true)) {
            return this.chartMetaData(true).axis_reversed
          }
        }
        return false
      }
    }
  },
  methods: {
    ...mapMutations(['select_parameter', 'select_parameter_compare', 'select_timestamp']),
    initChart: function () {
      this.chartBusy = true
      // Initialize chart
      this.chart = Highcharts.stockChart('chartContainer', {
        chart: {
          type: 'line',
          zoomType: 'xy',
          marginTop: 10,
          animation: false,
          alignTicks: false
        },
        plotOptions: {
          series: {
            allowPointSelect: false,
            cursor: 'pointer',
            gapSize: 3600000 * 3, // FIXME greater than 3 hours
            gapUnit: 'value'
          }
        },
        exporting: {
          buttons: {
            contextButton: {
              menuItems: [
                'downloadPNG',
                'downloadJPEG',
                'downloadPDF',
                'downloadSVG'
              ]
            }
          }
        },
        scrollbar: {
          enabled: false
        },
        navigator: {
          enabled: false
        },
        rangeSelector: {
          selected: 2,
          enabled: true,
          // inputBoxBorderColor: '#9BA7B7',
          inputEditDateFormat: '%d.%m.%Y',
          inputDateFormat: '%d. %b %Y',
          // height: 45,
          buttons: [
            { type: 'hour', count: 1, text: '1h' },
            { type: 'hour', count: 12, text: '12h' },
            { type: 'day', count: 1, text: '1d' },
            { type: 'week', count: 1, text: '1w' },
            { type: 'month', count: 1, text: '1m' },
            { type: 'month', count: 3, text: '3m' },
            { type: 'all', count: 1, text: 'All' }]
        },
        xAxis: {
          type: 'datetime',
          gridLineWidth: 1,
          tickPixelInterval: 50,
          ordinal: false,
          scrollbar: {
            enabled: false
          },
          events: {
            // setExtremes: syncExtremes
          }
        },
        tooltip: {
          enabled: true,
          backgroundColor: '#ffffff',
          shared: true,
          // valueDecimals: 2,
          crosshairs: true,
          animation: false
        },
        series: [{
          data: []
        }],
        credits: {
          enabled: false
        }
      })
      this.chartVisible = true
      this.chartBusy = false
    },
    beforeRefresh: function () {
      console.debug('chart widget: preparing refresh..')
      if (this.chart !== null) {
        // this.chartVisible = false
        // FIXME: Workaround, reducing width first, will be recalculated later
        this.chart.setSize(1, this.chart.chartHeight)
      }
    },
    refreshViewport: function (height) {
      const newHeight = height - this.$refs.header.offsetHeight
      this.paneHeight = newHeight
      // console.log('DEBUG: New chart pane height: ' + newHeight)
      if (this.chart !== null) {
        console.debug('chart widget: reflow chart due to pane resizing')
        this.chartBusy = true
        this.chart.setSize(null, newHeight)
        // this.chart.reflow()
        this.chartVisible = true
        this.chartBusy = false
      }
    },
    refresh: function () {
      this.select_parameter(this.selectedParameter)
      if (this.selectedParameterCompare !== null) {
        this.select_parameter_compare(this.selectedParameterCompare)
      }
    },
    toggleAutorefresh: function () {
      if (this.autorefresh) {
        if (this.refreshTask == null) {
          console.debug('chart widget: auto-refresh set up')
          this.refresh()
          // Create refresh task
          this.refreshTask = window.setInterval(() => {
            // Reload selected parameters
            this.refresh()
          }, REFRESH_INTERVAL)
        }
      } else {
        console.debug('chart widget: auto-refresh cleared')
        // Clear refresh task
        clearInterval(this.refreshTask)
        this.refreshTask = null
      }
    }
  }
}
</script>

<style scoped>
</style>
